import { amountValue, formatOrder } from "./money";
import { formatSepa } from "./sepa";
import { formatMoney } from "./money-format";
import { formatAddress, formatDateRange } from "./composite";
import { formatPollOption, POLL_VOTE_LABEL, pollVotes } from "./poll";
import { type Field, type FileRef, OTHER_CHOICE_ID } from "@fobu/schema";
import { isEmptyValue, toText } from "./expression";

const numberFmt = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 10 });

function choiceLabel(field: Field, id: string, other?: string): string {
  if (id === OTHER_CHOICE_ID) return other ?? "Sonstiges";
  if ("choices" in field) return field.choices.find((c) => c.id === id)?.label ?? id;
  return id;
}

/** Menschenlesbare Darstellung einer Antwort (Recall, E-Mails, Export, PDF). */
export function formatAnswer(field: Field, value: unknown, other?: string): string {
  if (isEmptyValue(value)) return "";
  switch (field.type) {
    case "single_choice":
    case "dropdown":
      return choiceLabel(field, toText(value), other);
    case "multiple_choice":
    case "ranking":
      return (value as string[]).map((id) => choiceLabel(field, id, other)).join(", ");
    case "yes_no":
      return value ? "Ja" : "Nein";
    case "consent":
      return value ? "Zugestimmt" : "Nicht zugestimmt";
    case "number":
    case "rating":
    case "nps":
    case "scale":
    case "slider": {
      const n = typeof value === "number" ? numberFmt.format(value) : toText(value);
      const unit = "unit" in field && field.unit ? ` ${field.unit}` : "";
      return n + unit;
    }
    case "date": {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(toText(value));
      return m ? `${m[3]}.${m[2]}.${m[1]}` : toText(value);
    }
    case "datetime": {
      const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(toText(value));
      return m ? `${m[3]}.${m[2]}.${m[1]}, ${m[4]} Uhr` : toText(value);
    }
    case "time":
      return `${toText(value)} Uhr`;
    case "matrix": {
      const rows = value as Record<string, string | string[]>;
      return field.rows
        .filter((r) => rows[r.id] !== undefined)
        .map((r) => {
          const cell = rows[r.id]!;
          const cols = (Array.isArray(cell) ? cell : [cell]).map((c) => field.columns.find((col) => col.id === c)?.label ?? c);
          return `${r.label}: ${cols.join(", ")}`;
        })
        .join("; ");
    }
    case "address":
      return formatAddress(field, value);
    case "amount": {
      const n = amountValue(value);
      return n === null ? toText(value) : formatMoney(Math.round(n * 100), field.currency);
    }
    case "order":
      return formatOrder(field, value, field.currency);
    case "sepa":
      return formatSepa(value);
    case "date_range":
      return formatDateRange(value);
    case "poll": {
      const votes = pollVotes(field, value);
      return field.options
        .filter((o) => votes[o.id])
        .map((o) => `${formatPollOption(o)}: ${POLL_VOTE_LABEL[votes[o.id]!]}`)
        .join("; ");
    }
    case "file_upload":
    case "signature":
      return (value as FileRef[]).map((f) => f.name).join(", ");
    case "group": {
      if (!Array.isArray(value)) return toText(value);
      return value
        .map((entry, i) => {
          const parts = field.fields
            .map((sub) => [sub.label, formatAnswer(sub, (entry as Record<string, unknown>)[sub.id])] as const)
            .filter(([, v]) => v !== "")
            .map(([l, v]) => `${l}: ${v}`);
          return `${field.itemLabel} ${i + 1}: ${parts.join(", ")}`;
        })
        .join("; ");
    }
    default:
      return toText(value);
  }
}
